// Kingdom 101 — War-Zone Global Ministry. Structured teaching content.

import { ICU_LEVEL_2 } from "./icuLevel2";

export const TEACHINGS = {
  course: "Kingdom 101",
  ministry: "War-Zone Global Ministry",
  description:
    "A foundational course on the Kingdom of God — our position, the benefits we already possess, the jurisdiction we carry, and Kingdom prosperity.",
  lessons: [
    {
      id: "intro",
      title: "Introduction: The Kingdom Foretold",
      summary: "The Kingdom of God as God's eternal reign, foretold from creation.",
      sections: [
        {
          title: "The Kingdom Foretold as God's Eternal Reign",
          body: "The concept of God's Kingdom is as old as creation, not a new idea introduced by Jesus. God established humanity as His image-bearers, intended to reflect and administer His rule on Earth. This dominion was meant to be exercised in fellowship with God, not in isolation. The fall disrupted this alignment but did not revoke God's rule; Scripture's narrative is about God restoring humanity's rightful place in His Kingdom.",
          points: [
            "Dominion as representation, not domination.",
            "Dominion flows from relationship, not ambition.",
            "Humanity was created to wear the crown of delegated authority.",
            "The fall fractured humanity's capacity, not God's dominion.",
            "The Kingdom was not introduced in the New Testament; it was reinstated in Christ.",
          ],
          scriptures: ["Genesis 1:26-28", "Psalm 8:4-6", "Psalm 103:19"],
        },
      ],
    },
    {
      id: "lesson-1",
      title: "Lesson 1: Our Position in the Kingdom",
      summary: "Understanding the Kingdom, entrance through Christ, and our identity as sons and heirs.",
      sections: [
        {
          title: "Understanding the Kingdom of God",
          body: "The Kingdom of God is a present, living reality of God's rule, characterized by His nature, order, and authority. It is not a physical place or a set of commands but the realm where God's will is lived out. The Kingdom is internal and perceived, not observed geographically or through physical boundaries. It transforms individuals from the inside out, impacting their thoughts, desires, and actions.",
          points: [
            "The Kingdom of God is not discovered by location, but revealed through perception.",
            "The Kingdom is a spiritual realm and mindset.",
            "Transformation begins inside, not outside.",
            "The Kingdom is not about escaping the world, but transforming it from the inside out.",
          ],
          scriptures: ["Luke 17:21", "Proverbs 23:7", "Romans 14:17", "Matthew 13:11"],
        },
        {
          title: "Kingdom Entrance Through Christ",
          body: "Entrance into the Kingdom is not through religious actions or moral effort but through spiritual birth. Jesus explained to Nicodemus that one must be 'born again,' meaning awakened to divine reality. This spiritual rebirth is initiated by the Holy Spirit and is essential for comprehending God's Kingdom. Salvation is a transfer from darkness to light, shifting spiritual perception and granting the authority to reign through identity in Christ.",
          points: [
            "To be born again is to be awakened to divine reality.",
            "Kingdom life is initiated by the Spirit, not human will.",
            "Salvation is a transfer from the dominion of darkness to the Kingdom of the Son.",
            "Rebirth opens your eyes; righteousness gives you the authority to reign.",
          ],
          scriptures: ["John 3:3, 5", "Colossians 1:13", "Romans 5:17", "Titus 3:5", "Ephesians 2:5-6"],
        },
        {
          title: "Our Position as Sons and Heirs",
          body: "Upon being born again, believers are repositioned from forgiven sinners to adopted children and heirs of God. This restoration of identity, facilitated by the Holy Spirit, changes our status and nature, aligning us with God's likeness. As heirs, everything Christ has access to is ours by relationship, not by effort. This sonship is legal, relational, and eternal, granting full rights and shared essence with God.",
          points: [
            "Salvation is the restoration of identity, making us children of God.",
            "Salvation changes your status; the Spirit changes your nature.",
            "As heirs, we receive fellowship, authority, and divine supply by relationship.",
            "Servants work for God; sons work with God.",
            "Heaven is our seat of authority; Earth is our assignment.",
          ],
          scriptures: ["Romans 8:16-17", "Galatians 4:6-7", "Ephesians 2:6", "John 1:12", "1 John 3:1", "John 5:18"],
        },
        {
          title: "Citizenship in the Kingdom",
          body: "Our true citizenship is in heaven, meaning our allegiance, identity, and governmental covering belong to another Kingdom. Citizenship determines our conduct, as where we belong dictates how we behave. Unlike the world's emphasis on survival and competition, the Kingdom teaches rest, stewardship, and trust. As Christ's ambassadors, we represent His policies and culture, living from heaven today, not just someday.",
          points: [
            "Citizenship determines conduct — where you belong determines how you behave.",
            "The Kingdom teaches rest, stewardship, trust, and reign.",
            "Ambassadors live according to the nation they represent.",
            "We do not live for heaven someday; we live from heaven today.",
          ],
          scriptures: ["Philippians 3:20", "2 Corinthians 5:20", "Ephesians 2:19", "Galatians 4:6-7"],
        },
        {
          title: "Identity in Christ",
          body: "Our identity in Christ is one of recreation, not improvement; we are entirely new creations. This transformation is a divine rebirth that establishes our confidence, authority, and resilience. Peter calls us a chosen, royal, priestly, holy nation, signifying that God desires us, we share His authority, carry His presence, and are set apart for His purpose. Kingdom expression requires both identity and authority walking hand-in-hand, balancing humility with rulership.",
          points: [
            "Identity determines expression.",
            "In Christ we are not improved versions of our old selves; we are entirely new creations.",
            "Chosen: God desired you. Royal: you share His authority. Priesthood: you carry His presence. Holy: you are set apart.",
            "The Kingdom is fully expressed when identity and authority walk hand in hand.",
            "Authority without identity is pride; identity without authority is passivity.",
          ],
          scriptures: ["2 Corinthians 5:17", "1 Peter 2:9", "Ephesians 4:24", "Romans 8:29"],
        },
        {
          title: "Living From Kingdom Awareness",
          body: "Living from Kingdom reality means basing our lives on our true seat with Christ in heavenly places, rather than temporary earthly circumstances. Kingdom awareness prioritizes the more true reality of the Spirit, making the invisible our reference point. We are called to pray for God's Kingdom to come and His will to be done on Earth, reflecting a posture of alignment rather than escape. This mindset shifts our reactions, stabilizes our peace, and strengthens our authority.",
          points: [
            "Kingdom awareness is when the invisible becomes your reference point.",
            "We do not pray for escape; we pray for alignment.",
            "Living from heaven downward means responding from spiritual reality.",
            "Mindset shifts reactions, stabilizes peace, and strengthens authority.",
          ],
          scriptures: ["Matthew 6:10", "Luke 11:2", "Colossians 3:1-2"],
        },
        {
          title: "Conclusion: Identity in the Kingdom Is Established in the Mind",
          body: "Our position in the Kingdom is experienced through the mind, where belief, perception, and thought shape our identity and interpret reality. Faith awakens us to the identity already given in Christ, and spiritual maturity is measured by this awareness. When our mind aligns with the spirit, the Kingdom becomes visible, and our identity is reinforced.",
          points: [
            "Identity is embodied and begins internally; the mind is the seat of awareness.",
            "Identity is shaped through belief + repetition + reinforcement.",
            "These truths must move from information to formation.",
            "Transformation is identity realization, not behavior modification.",
            "The Kingdom starts with identity, which is established in the mind.",
          ],
          scriptures: ["Proverbs 23:7", "Luke 17:21", "Romans 12:2"],
        },
      ],
    },
    {
      id: "lesson-2",
      title: "Lesson 2: The Benefits of the Kingdom of God",
      summary: "The benefits we already possess: righteousness, peace, provision, deliverance, healing, sonship, authority, and understanding.",
      sections: [
        {
          title: "Kingdom Righteousness — Justification",
          body: "The first benefit of the Kingdom is righteousness, centered on justification. Justification is a legal declaration of being righteous, acquitted, and in right standing with God, not something earned. It is the great exchange where Christ takes our sin and gives us His righteousness, canceling guilt and establishing a new status. Righteousness is received, not earned, and it restores confidence in our sonship, removing shame, fear, and striving.",
          points: [
            "Justification is a legal declaration.",
            "Righteousness is not earned — righteousness is received.",
            "The opposite of righteousness is condemnation.",
            "Justification restores confidence in sonship and removes distance.",
          ],
          scriptures: ["2 Corinthians 5:21", "Romans 8:1", "Romans 10:10", "Matthew 6:33", "2 Corinthians 3:9", "Romans 8:30"],
        },
        {
          title: "Kingdom Peace — Restored Union & Restful Stability",
          body: "Peace in the Kingdom is the presence of divine alignment and restored relationship with God, not just the absence of conflict. It is established in the spirit through reconciliation. Peace is a person, found in Christ, who dwells within us. This peace is authoritative, governs, and provides restful stability within storms, stemming from the confidence that Christ has overcome.",
          points: [
            "Peace is the presence of divine alignment.",
            "Peace is not a feeling; peace is a person.",
            "Peace is stability within storms, not immunity from them.",
            "The Greek eirēnē means to be joined together, made whole.",
            "Righteousness makes us one with God; peace allows us to live from that oneness.",
          ],
          scriptures: ["John 16:33", "Romans 5:1", "Luke 2:14", "Galatians 5:22", "Colossians 1:20"],
        },
        {
          title: "Kingdom Provision — Sustained and Supplied",
          body: "Kingdom provision is rooted in relationship and trust, not anxiety or striving. It is not merely for survival but for purpose and assignment, flowing from God's abundance. Provision is inherited, not earned, and is the evidence of the Father's visible care. Kingdom provision is for assignment, not accumulation, and it resources our calling.",
          points: [
            "Provision is not earned — provision is inherited.",
            "Provision flows when we trust God as Father.",
            "Kingdom provision is not for accumulation — it is for assignment.",
            "Provision is the Father's care made visible.",
          ],
          scriptures: ["Matthew 6:31-32", "Philippians 4:19", "Luke 12:24", "2 Corinthians 9:8", "Ephesians 3:20", "2 Peter 1:3", "Romans 8:32"],
        },
        {
          title: "Kingdom Deliverance — Freedom & Transfer of Authority",
          body: "Deliverance in the Kingdom is not just escape but a relocation into something new — a change of jurisdiction and a legal transfer of authority from darkness to the Kingdom of the Son. It is a governmental transition when God breaks the legal claim of darkness. Spiritual warfare is the enforcement of Christ's authority, fighting from the victory already secured.",
          points: [
            "Deliverance is a change of jurisdiction.",
            "Deliverance is not just release — deliverance is relocation.",
            "The Greek rhuomai means to rescue by drawing out of danger into safety.",
            "Deliverance is the visible proof of God's dominion.",
            "Freedom is release into purpose.",
          ],
          scriptures: ["Colossians 1:13", "Luke 1:74", "2 Corinthians 1:10", "Romans 6:14", "Psalm 34:19", "2 Timothy 4:18", "1 Thessalonians 1:10", "2 Peter 2:9"],
        },
        {
          title: "Kingdom Healing — Restoration & Evidence of God's Reign",
          body: "Healing in the Kingdom is a message and a demonstration of God's reign, not merely a miracle. The Greek iaomai means to heal by making whole, restoring what has been damaged to its original design. Healing is restoration, where God makes the broken whole again, reversing the works of darkness. It is secured as part of Christ's covenant package of salvation.",
          points: [
            "Healing is a message and a demonstration of the Kingdom.",
            "Healing is restoration — God makes the broken whole again.",
            "Healing is the evidence that the Kingdom is present.",
            "Healing is secured as part of the covenant package of salvation.",
            "Healing is the evidence that the King has entered the room.",
          ],
          scriptures: ["Luke 9:2", "Acts 10:38", "1 Peter 2:24", "Luke 5:17", "Luke 8:47-48", "Hebrews 12:13"],
        },
        {
          title: "Kingdom Sonship — Identity & Inheritance",
          body: "Sonship is the heart of the Kingdom, built on sons and daughters who share the Father's nature, access, and inheritance. Through adoption by the Spirit, we are positioned as heirs with full rights and status, removing fear and establishing relational identity. As co-heirs with Christ, we have access to the Father's resources now.",
          points: [
            "Sonship begins with identity and adoption.",
            "Sonship is the restoration of image.",
            "Inheritance is access to the full resources of the Father now.",
            "Sonship is the foundation of confidence in the Kingdom.",
            "Kingdom adoption is legal, not symbolic.",
          ],
          scriptures: ["Romans 8:15", "Romans 8:17", "Galatians 4:4-5", "Ephesians 1:5", "Romans 8:23", "John 5:18"],
        },
        {
          title: "Kingdom Authority — Legal Right & Spiritual Power",
          body: "Kingdom authority is legal positioning derived from identity, not just spiritual power. Jesus granted authority — delegated jurisdiction, legal right, and authorized rule — to overcome the enemy. This authority is rooted in identity as sons and is received, not worked up. When we speak in alignment with God's Word and Spirit, heaven backs our decree.",
          points: [
            "Authority is legal positioning that flows from identity.",
            "Authority is the right and the power to rule.",
            "We command darkness as sons, not servants.",
            "We authorize breakthrough, we do not beg for it.",
          ],
          scriptures: ["Luke 10:19", "John 1:12", "Matthew 10:1", "Ephesians 1:21", "Colossians 1:13", "Matthew 7:29", "Acts 1:7"],
        },
        {
          title: "Spiritual Understanding — Awareness Given by the Spirit",
          body: "Understanding God's things is not achieved by intellect alone but is revealed by the Spirit. The Greek oida speaks to direct perception and spirit-given awareness. Without the Spirit, the Kingdom appears invisible or foolish, but with Him it becomes the most real truth. Revelation is what makes the invisible real, bringing Scripture alive and anchoring identity and purpose.",
          points: [
            "The Kingdom is revealed, not grasped by intellect.",
            "Spiritual understanding is Spirit-given awareness.",
            "Revelation makes the invisible real.",
            "Spiritual understanding enables discernment.",
          ],
          scriptures: ["1 Corinthians 2:12", "John 14:17", "1 John 5:20", "John 7:29", "1 Corinthians 8:1-2"],
        },
        {
          title: "Conclusion: The Benefits Are Immediate and Simultaneous",
          body: "The benefits of the Kingdom are granted the moment we enter by faith in Christ, not earned over time. Righteousness, peace, provision, deliverance, healing, sonship, authority, and spiritual understanding are realities we already possess. The Kingdom unfolds as a whole, as Christ in us is the hope of glory. Our journey is not achieving these benefits but learning to live from them.",
          points: [
            "Benefits are granted immediately upon entering the Kingdom.",
            "The Kingdom arrives as a whole, not benefit by benefit.",
            "All benefits are expressions of one reality: Christ in us.",
            "We are learning how blessed we already are — receiving, resting, awakening.",
          ],
          scriptures: ["Colossians 1:13"],
        },
      ],
    },
    {
      id: "lesson-3",
      title: "Lesson 3: Jurisdiction and the Kingdom of God",
      summary: "Jurisdiction, the speech of kings, God's dominion, our spiritual seat, and Kingdom culture.",
      sections: [
        {
          title: "Jurisdiction and the Speech of Kings",
          body: "Every kingdom functions through jurisdiction, which defines where authority begins and ends — it means 'to say the law.' Jurisdiction is the power to declare, establish, and enforce what is lawful within a territory. Kings rule by decree; their speech exercises power and determines reality. Belief produces internal jurisdiction, and speech is where internal dominion becomes external reality.",
          points: [
            "Jurisdiction means 'to say the law.'",
            "Your jurisdiction is in your decree.",
            "Decrees set spiritual law into motion, backed by heaven.",
            "Binding and loosing are governmental acts of jurisdictional authority.",
            "Belief is internal jurisdiction; speech is external enforcement.",
          ],
          scriptures: ["Job 22:28 (AMP)", "Ecclesiastes 8:4 (KJV)", "Matthew 18:18", "Mark 11:23", "2 Corinthians 4:13"],
        },
        {
          title: "God's Dominion and Kingdom Authority",
          body: "Dominion is the supreme right to rule, signifying ownership and sovereign control. It is the King's legal right to determine what exists and how it functions. God's Kingdom is an eternal government that reigns everywhere, at all times, without rival. Christ restored the dominion forfeited by Adam, allowing dominion to be delegated again to believers.",
          points: [
            "Dominion is the supreme right to rule.",
            "God does not simply reign — He reigns everywhere, at all times, without rival.",
            "Christ restored dominion, allowing it to be delegated again.",
            "We do not fight for authority — we fight from authority.",
            "The believer learns to become aware, not merely powerful.",
          ],
          scriptures: ["Psalm 103:19", "Daniel 4:3 (NIV)", "Matthew 28:18 (NIV)"],
        },
        {
          title: "Spiritual Jurisdiction — Our Seat, Sphere, and Reach",
          body: "Spiritual jurisdiction is a positional reality where Christ is enthroned above all rule and authority. Authority flows from our seat with Christ in heavenly places, granted at salvation through union with Him. Spiritual warfare is jurisdictional, legislating downward from the throne room. Our jurisdiction is wherever God assigns us: household, body, mind, atmosphere, calling, and territory of influence.",
          points: [
            "Christ is enthroned above every rank of authority.",
            "Authority is inherited by position, not earned.",
            "Spiritual warfare is the enforcement of Christ's victory, not the pursuit of one.",
            "Jurisdiction is heavenly (seated with Christ), legal (backed by His dominion), and operational (expressed through decree).",
            "Our jurisdiction is wherever God has assigned us.",
          ],
          scriptures: ["Ephesians 1:20-23 (NIV)", "Ephesians 2:6 (NIV)", "Ephesians 6:12 (NIV)", "Colossians 2:10 (NIV)"],
        },
        {
          title: "Kingdom Culture vs. World System vs. Religious System",
          body: "Every kingdom has a cultivated culture of thinking, responding, and living. The Kingdom of God's culture reflects the King's nature, characterized by surrendered alignment to His will. The world system is governed by self-rule and prioritizes self as source, leading to instability. The religious system appears spiritual but is man-centered, emphasizing performance over transformation.",
          points: [
            "Culture reveals the kingdom that governs you.",
            "Kingdom culture: surrendered alignment to the will of the King.",
            "World system: self-rule, self as source.",
            "Religious system: man-centered, outward performance.",
            "The battle for culture is the battle for jurisdiction of the mind.",
          ],
          scriptures: ["John 18:36", "Romans 12:2", "James 4:4", "2 Corinthians 5:17", "Romans 8:14", "1 John 2:15-17", "Galatians 3:28", "Matthew 23:27"],
        },
        {
          title: "Conclusion: Your Jurisdiction Is Your Decree",
          body: "Jurisdiction is a function exercised by speech, releasing authority when the believer speaks in agreement with God's will. Kings do not strive to enforce their rule; they speak decrees that establish reality. When believers remain silent, their God-entrusted jurisdiction goes unexercised. Faith is confirmed in speech, and our words become a legislative instrument of the Kingdom when they align with God's words.",
          points: [
            "Dominion is exercised by speech, not intention or silence.",
            "Your jurisdiction is your decree.",
            "Silence is forfeiture; establishment follows declaration.",
            "Faith is confirmed in speech; belief without declaration is incomplete.",
            "To exercise jurisdiction: Discern, Align, Declare, Expect.",
          ],
          scriptures: ["Job 22:28", "Ecclesiastes 8:4", "Mark 11:23", "2 Corinthians 4:13"],
        },
      ],
    },
    {
      id: "lesson-4",
      title: "Lesson 4: Prosperity and the Kingdom of God",
      summary: "Kingdom prosperity that begins in the soul, the power to prosper, and generosity.",
      sections: [
        {
          title: "Kingdom Prosperity Begins in the Soul",
          body: "Kingdom prosperity is not about accumulating wealth or outward success but about moving forward on God's intended path — aligned movement. It is rooted in hope and confident expectation, meaning advancement agrees with God's order and rhythm. Prosperity flows from the inside out, sustained by internal wholeness and a restored soul.",
          points: [
            "Prosperity is aligned movement.",
            "The Greek euodoó means 'to be helped along the right road.'",
            "Prosperity flows from the inside out.",
            "Your soul (mind, will, emotions) is the seat of your prosperity.",
            "The Word of God converts the soul.",
          ],
          scriptures: ["3 John 1:2", "Psalm 23:3", "Psalm 19:7", "Isaiah 58:11"],
        },
        {
          title: "God Gives Power to Prosper",
          body: "Prosperity in the Kingdom is God-enabled movement, as God gives power to get wealth. Power means ability, capacity, authorization, and the competence to act. It is what we are able to build, sustain, and multiply without losing peace. The blessing of the Lord makes rich without sorrow, marked by unity of soul.",
          points: [
            "Prosperity is the alignment of capacity with divine intention.",
            "The blessing of the LORD makes rich, and He adds no sorrow with it.",
            "Kingdom prosperity is marked by unity of soul.",
            "Prosperity is not chased — it is inherited through alignment.",
          ],
          scriptures: ["Deuteronomy 8:18", "Proverbs 10:22", "Psalm 1:1-3", "Joshua 1:8", "Proverbs 3:5-6", "Proverbs 16:3", "Isaiah 48:17"],
        },
        {
          title: "Generosity in the Kingdom",
          body: "Kingdom giving is characterized by intentionality, not reluctance or compulsion. Giving must be a decision from the heart, reflecting identity and alignment. Reluctant giving is not in faith and hurts peace; compulsive giving is payment, not Kingdom giving. Cheerful giving is the sound of a free person and reflects the wealth of the Kingdom.",
          points: [
            "Kingdom giving is intentional, not reluctant, not under compulsion.",
            "Give from identity, not insecurity.",
            "If giving hurts your peace, it is not in faith.",
            "Cheerful giving is the sound of a free person — and freedom is the wealth of the Kingdom.",
            "Sowing is Kingdom legislation.",
          ],
          scriptures: ["2 Corinthians 9:6-8", "2 Corinthians 9:10-12", "Luke 6:38", "Deuteronomy 15:10", "Proverbs 11:24-25", "Acts 20:35", "Matthew 19:21"],
        },
        {
          title: "Conclusion: The Seed and the Sower",
          body: "The Kingdom operates by seed, with the believer's heart as the ground. Not every seed fails because the seed is weak, but because the ground is compromised by the cares of this world or the deceitfulness of wealth. Prosperity must be taught as stewardship of the soul, not pursuit of wealth. We prosper as our soul prospers — wholeness is the goal.",
          points: [
            "The heart of the believer is the ground for the seed of the Kingdom.",
            "'The cares of this world' and 'the deceitfulness of wealth' are internal conditions.",
            "Prosperity must be taught as the stewardship of the soul.",
            "Prosperity cannot outrun the development of the soul.",
            "When the soul is whole, increase has no opposition.",
          ],
          scriptures: ["Matthew 13:22", "3 John 2", "2 Corinthians 9:7"],
        },
      ],
    },
  ],
};

export const COURSES = [
  { id: "kingdom-101", ...TEACHINGS },
  ICU_LEVEL_2,
];
